import { useState } from 'react'
import { changePassword } from '../../services/authService.js'
import PasswordStrengthIndicator from './PasswordStrengthIndicator.jsx'
import { validatePasswordStrength, checkPasswordMatch } from '../../utils/passwordValidation.js'

/**
 * Change Password Component
 * 
 * Allows logged-in users to change their password
 */
const ChangePassword = ({ onSuccess }) => {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [validationError, setValidationError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setValidationError(null)
    setSuccess(false)

    // Validate password strength
    const passwordValidation = validatePasswordStrength(newPassword)
    if (!passwordValidation.valid) {
      setValidationError(passwordValidation.errors.join(' '))
      return
    }

    // Check password match
    const matchCheck = checkPasswordMatch(newPassword, confirmPassword)
    if (!matchCheck.match) {
      setValidationError(matchCheck.error)
      return
    }

    // Check if old and new passwords are different
    if (oldPassword === newPassword) {
      setValidationError('Le nouveau mot de passe doit être différent de l\'ancien.')
      return
    }

    setIsLoading(true)

    try {
      const result = await changePassword(oldPassword, newPassword)
      
      if (result.success) {
        setSuccess(true)
        setError(null)
        setOldPassword('')
        setNewPassword('')
        setConfirmPassword('')
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          setTimeout(() => {
            onSuccess()
          }, 2000)
        }
      } else {
        setError(result.error)
        setSuccess(false)
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la modification du mot de passe')
      setSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Modifier le mot de passe
      </h2>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          Mot de passe modifié avec succès !
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {(error || validationError) && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error || validationError}
          </div>
        )}

        <div>
          <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Ancien mot de passe
          </label>
          <input
            id="oldPassword"
            type="password"
            required
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="••••••••"
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Nouveau mot de passe
          </label>
          <input
            id="newPassword"
            type="password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="••••••••"
            disabled={isLoading}
          />
          <PasswordStrengthIndicator password={newPassword} />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirmer le nouveau mot de passe
          </label>
          <input
            id="confirmPassword"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="••••••••"
            disabled={isLoading}
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="mt-1 text-sm text-red-600">Les mots de passe ne correspondent pas</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || !oldPassword || !newPassword || !confirmPassword}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Modification...
            </>
          ) : (
            'Modifier le mot de passe'
          )}
        </button>
      </form>
    </div>
  )
}

export default ChangePassword

